import type { Device } from 'src/types/Device';
import type { PVSetupTypes } from 'src/components/Consumption/PVSetupEnergy/pv_setup_helper';

import { devices_config } from '@devices/index';

import { MAIN_ENERGY_METER_KEY, MAIN_ENERGY_METER_TYPES } from './main-energy-meter-helpers';

export type SolarMeterRole = 'grid' | 'solar';

export type SourceRelayUsage = 'grid_source' | 'generated_energy';

export type ApplianceSyncUsage = SourceRelayUsage | 'other';

export type PvModeMeterLists = {
	gridDevices: string[];
	solarDevices: string[];
};

/** Cloud/PHP often stores these as objects (`{ "0": "id" }`) instead of arrays. */
export type StoredMeterIdList = string[] | Record<string, string>;

export type StoredPvModeMeterLists = {
	gridDevices?: StoredMeterIdList;
	solarDevices?: StoredMeterIdList;
};

export type PvConfigurations = Partial<Record<PVSetupTypes, StoredPvModeMeterLists>>;

export const normalizeMeterIdList = (list: StoredMeterIdList | null | undefined): string[] => {
	if (list == null) return [];
	const values = Array.isArray(list) ? list : Object.values(list);
	return values.filter((id): id is string => typeof id === 'string' && id.length > 0);
};

export type DeviceEnergyDesignation = {
	isMainEnergyMeter: boolean;
	solarRole: SolarMeterRole | null;
	leftoverSourceUsage: SourceRelayUsage | null;
};

export type ApplianceTypeSyncChange = {
	deviceId: string;
	usage: ApplianceSyncUsage;
};

export const GRID_SOURCE_APPLIANCE_TYPE = 11;
export const GENERATED_ENERGY_APPLIANCE_TYPE = 12;

export const GRID_SOURCE_USAGE: SourceRelayUsage = 'grid_source';
export const GENERATED_ENERGY_USAGE: SourceRelayUsage = 'generated_energy';

export const isSourceRelayUsage = (usage?: string): usage is SourceRelayUsage =>
	usage === GRID_SOURCE_USAGE || usage === GENERATED_ENERGY_USAGE;

/**
 * Inclusive Energy Category list: configs that measure power (`isPM`).
 * That flag is set on emeter, power_meter, and PM relays/plugs — not on
 * input readers, radar/presence, cameras, or other sensors.
 */
export const isEnergyCategoryEligibleDevice = (device: { type?: string }): boolean =>
	devices_config[device.type]?.isPM === true;

export const isEligibleSolarMeterDevice = (
	device: Pick<Device, 'category' | 'shared' | 'type'>,
	setupType: PVSetupTypes,
	role: SolarMeterRole,
	isPm: boolean,
): boolean => {
	if (device.shared || !isPm) return false;

	if (role === 'solar' && setupType === 'BalconyPV') {
		return /^(power_meter|relay|emeter)$/.test(device.category || '');
	}

	return device.category === 'emeter';
};

export const getSolarMeterRoleHint = (
	setupType: PVSetupTypes,
	role: SolarMeterRole,
): { key: string; default: string } => {
	if (role === 'grid') {
		if (setupType === 'BackUP') {
			return {
				key: 'solar_setup_hint_backup_grid',
				default:
					'Select the meter on the grid supply. This is usually the same device as the main energy meter.',
			};
		}
		if (setupType === 'BalconyPV') {
			return {
				key: 'solar_setup_hint_balcony_grid',
				default:
					'Select the meter that measures grid consumption for this home. It is often already set as the main energy meter.',
			};
		}
		return {
			key: 'solar_setup_hint_parallel_grid',
			default:
				'Select the meter on the grid connection. This is usually the same device as the main energy meter.',
		};
	}

	if (setupType === 'BackUP') {
		return {
			key: 'solar_setup_hint_backup_solar',
			default:
				'Select the meter on the inverter output (total house energy), not the solar panels themselves.',
		};
	}
	if (setupType === 'BalconyPV') {
		return {
			key: 'solar_setup_hint_balcony_solar',
			default:
				'Select the plug-in PV device. It should report negative power when it is generating.',
		};
	}
	return {
		key: 'solar_setup_hint_parallel_solar',
		default: 'Select the meter that measures photovoltaic generation for this home.',
	};
};

export const getActiveSolarSetupType = (
	homeData: {
		net_metering_type?: PVSetupTypes;
		pv_configurations?: PvConfigurations;
	} | null,
): PVSetupTypes => {
	const configuredType = homeData?.net_metering_type;
	if (configuredType === 'Parallel' || configuredType === 'BackUP' || configuredType === 'BalconyPV') {
		return configuredType;
	}
	return 'Parallel';
};

export const getPvModeMeterIds = (
	homeData: { pv_configurations?: PvConfigurations } | null,
	setupType: PVSetupTypes,
): PvModeMeterLists => {
	const mode = homeData?.pv_configurations?.[setupType];
	return {
		gridDevices: normalizeMeterIdList(mode?.gridDevices),
		solarDevices: normalizeMeterIdList(mode?.solarDevices),
	};
};

export const getConfiguredMeterIdsByRole = (
	configurations: PvConfigurations | undefined,
	role: SolarMeterRole,
): string[] => {
	const listKey = role === 'grid' ? 'gridDevices' : 'solarDevices';
	const ids = new Set<string>();

	Object.values(configurations || {}).forEach((mode) => {
		normalizeMeterIdList(mode?.[listKey]).forEach((id) => ids.add(id));
	});

	return [...ids];
};

export const isSolarSetupConfigured = (lists: PvModeMeterLists): boolean =>
	lists.gridDevices.length > 0 && lists.solarDevices.length > 0;

export const getDeviceEnergyDesignation = (
	deviceId: string,
	relayUsage: string | undefined,
	mainEnergyMeterId: string | null,
	gridDeviceIds: string[],
	solarDeviceIds: string[],
): DeviceEnergyDesignation => {
	const isMainEnergyMeter = Boolean(mainEnergyMeterId && mainEnergyMeterId === deviceId);
	const solarRole: SolarMeterRole | null = solarDeviceIds.includes(deviceId)
		? 'solar'
		: gridDeviceIds.includes(deviceId)
			? 'grid'
			: null;
	const leftoverSourceUsage =
		!isMainEnergyMeter && solarRole === null && isSourceRelayUsage(relayUsage) ? relayUsage : null;

	return { isMainEnergyMeter, solarRole, leftoverSourceUsage };
};

export const isDeviceEnergyDesignated = (designation: DeviceEnergyDesignation): boolean =>
	designation.isMainEnergyMeter || designation.solarRole !== null;

export const getHomeEnergyDesignationForDevice = (
	device: Pick<Device, 'id' | 'relay_usage'>,
	homeData: {
		net_metering_type?: PVSetupTypes;
		pv_configurations?: PvConfigurations;
		main_energy_meter?: string;
	} | null,
): DeviceEnergyDesignation => {
	const lists = getPvModeMeterIds(homeData, getActiveSolarSetupType(homeData));
	return getDeviceEnergyDesignation(
		device.id,
		device.relay_usage,
		homeData?.[MAIN_ENERGY_METER_KEY] || null,
		lists.gridDevices,
		lists.solarDevices,
	);
};

export const getMainEnergyMeterPromptCandidate = (
	gridDevices: Device[],
	mainEnergyMeterId: string | null,
	eligibleMainMeterTypes: string[] = MAIN_ENERGY_METER_TYPES,
): Device | null => {
	if (gridDevices.some((device) => device.id === mainEnergyMeterId)) return null;

	const eligible = gridDevices.filter((device) => eligibleMainMeterTypes.includes(device.type));
	if (eligible.length === 0) return null;
	if (gridDevices.length === 1) return eligible[0] ?? null;
	return eligible[0] ?? null;
};

export const planSolarSetupApplianceTypes = ({
	previousGridIds,
	previousSolarIds,
	nextGridIds,
	nextSolarIds,
	mainEnergyMeterId,
}: {
	previousGridIds: string[];
	previousSolarIds: string[];
	nextGridIds: string[];
	nextSolarIds: string[];
	mainEnergyMeterId: string | null;
}): ApplianceTypeSyncChange[] => {
	const nextUsage = new Map<string, ApplianceSyncUsage>();

	nextGridIds.forEach((id) => nextUsage.set(id, GRID_SOURCE_USAGE));
	nextSolarIds.forEach((id) => nextUsage.set(id, GENERATED_ENERGY_USAGE));
	if (mainEnergyMeterId && !nextUsage.has(mainEnergyMeterId)) {
		nextUsage.set(mainEnergyMeterId, GRID_SOURCE_USAGE);
	}

	const previousIds = new Set([...previousGridIds, ...previousSolarIds]);
	const changes: ApplianceTypeSyncChange[] = [];

	nextUsage.forEach((usage, deviceId) => {
		changes.push({ deviceId, usage });
	});
	previousIds.forEach((deviceId) => {
		if (!nextUsage.has(deviceId)) {
			changes.push({ deviceId, usage: 'other' });
		}
	});

	return changes;
};

export const planMainMeterApplianceTypes = ({
	previousMainId,
	nextMainId,
	solarGridIds,
}: {
	previousMainId: string | null;
	nextMainId: string | null;
	solarGridIds: string[];
}): ApplianceTypeSyncChange[] => {
	const changes: ApplianceTypeSyncChange[] = [];

	if (nextMainId) {
		changes.push({ deviceId: nextMainId, usage: GRID_SOURCE_USAGE });
	}

	if (previousMainId && previousMainId !== nextMainId && !solarGridIds.includes(previousMainId)) {
		changes.push({ deviceId: previousMainId, usage: 'other' });
	}

	return changes;
};

export const removeDeviceFromPvConfigurations = (
	configurations: PvConfigurations,
	deviceId: string,
): PvConfigurations => {
	const next: PvConfigurations = {};

	(Object.keys(configurations) as PVSetupTypes[]).forEach((setupType) => {
		const mode = configurations[setupType];
		if (!mode) return;
		const gridDevices = normalizeMeterIdList(mode.gridDevices).filter((id) => id !== deviceId);
		const solarDevices = normalizeMeterIdList(mode.solarDevices).filter((id) => id !== deviceId);
		if (gridDevices.length === 0 && solarDevices.length === 0) return;
		next[setupType] = { gridDevices, solarDevices };
	});

	return next;
};
